/**
 * Testimonials Carousel Custom Element
 * Auto-cycling carousel with pause on hover and accessibility support
 */

class TestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = this.querySelectorAll('.slider-dot');

    this.currentPage = 1;
    this.totalPages = 1;
    this.totalItems = this.sliderItems.length;
    this.desktopColumns = parseInt(this.dataset.columns) || 2;
    this.slidesPerPage = 1;
    this.sliderItemOffset = 0;
    this.autoplay = null;
    this.autoplaySpeed = (parseInt(this.dataset.speed) || 5) * 1000;
    this.isAutoplayEnabled = this.dataset.autoplay === 'true';
    this.isPaused = false;

    if (!this.slider || this.sliderItems.length === 0) return;

    this.init();
  }

  init() {
    this.totalItems = this.sliderItems.length;
    this.initPages();

    // Resize observer for responsive updates
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    // Scroll listener for page updates
    this.slider.addEventListener('scroll', this.debounce(this.update.bind(this), 50));

    // Navigation buttons
    this.prevButton?.addEventListener('click', this.onPrevClick.bind(this));
    this.nextButton?.addEventListener('click', this.onNextClick.bind(this));

    // Dot navigation
    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.onDotClick.bind(this));
    });

    // Autoplay controls
    if (this.isAutoplayEnabled) {
      this.setupAutoplay();
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (el) => el.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = 1;
      this.updateDots();
      return;
    }

    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

    // Check if we're on desktop (750px+) and use configured columns
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (isDesktop) {
      this.slidesPerPage = this.desktopColumns;
    } else {
      this.slidesPerPage = 1;
    }

    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // Ensure currentPage doesn't exceed totalPages after resize
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    this.updateDots();
  }

  update() {
    if (!this.slider || !this.sliderItemOffset) return;

    const scrollPosition =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    this.currentPage = Math.max(1, Math.min(scrollPosition, this.totalPages));

    this.updateDots();
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const position = index + 1;

      // Show/hide dots based on totalPages (different on mobile vs desktop)
      if (position <= this.totalPages) {
        dot.style.display = '';
        if (position === this.currentPage) {
          dot.classList.add('slider-dot--active');
        } else {
          dot.classList.remove('slider-dot--active');
        }
      } else {
        dot.style.display = 'none';
        dot.classList.remove('slider-dot--active');
      }
    });
  }

  onDotClick(event) {
    const position = parseInt(event.target.dataset.position);
    if (position && position !== this.currentPage) {
      this.currentPage = position;
      this.updateDots();
      this.scrollToPage(position);
    }
  }

  onPrevClick(event) {
    event.preventDefault();

    if (this.currentPage === 1) {
      // Loop to last
      this.currentPage = this.totalPages;
    } else {
      this.currentPage--;
    }
    this.updateDots();
    this.scrollToPage(this.currentPage);
  }

  onNextClick(event) {
    event.preventDefault();

    if (this.currentPage >= this.totalPages) {
      // Loop to first
      this.currentPage = 1;
    } else {
      this.currentPage++;
    }
    this.updateDots();
    this.scrollToPage(this.currentPage);
  }

  scrollToPage(page) {
    // Get the actual position of the target slide
    const targetIndex = page - 1;
    if (this.sliderItemsToShow[targetIndex]) {
      const targetPosition = this.sliderItemsToShow[targetIndex].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.setSlidePosition(targetPosition);
    }
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: 'smooth',
    });
  }

  setupAutoplay() {
    // Respect reduced motion preference
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      this.isPaused = true;
      this.updateAutoplayButton();
      return;
    }

    // Listen for reduced motion changes
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches) {
        this.pause();
        this.isPaused = true;
        this.updateAutoplayButton();
      }
    });

    // Pause on hover/focus
    this.addEventListener('mouseenter', this.pause.bind(this));
    this.addEventListener('mouseleave', this.resumeIfNotPaused.bind(this));
    this.addEventListener('focusin', this.pause.bind(this));
    this.addEventListener('focusout', this.resumeIfNotPaused.bind(this));

    // Start autoplay
    this.play();
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(() => this.autoRotate(), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  resumeIfNotPaused() {
    if (!this.isPaused && this.isAutoplayEnabled) {
      this.play();
    }
  }

  toggleAutoplay() {
    this.isPaused = !this.isPaused;
    this.updateAutoplayButton();

    if (this.isPaused) {
      this.pause();
    } else {
      this.play();
    }
  }

  updateAutoplayButton() {
    if (!this.autoplayButton) return;

    if (this.isPaused) {
      this.autoplayButton.classList.add('slideshow__autoplay--paused');
      this.autoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings?.playSlideshow || 'Play slideshow'
      );
    } else {
      this.autoplayButton.classList.remove('slideshow__autoplay--paused');
      this.autoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow'
      );
    }
  }

  autoRotate() {
    if (this.currentPage >= this.totalPages) {
      this.currentPage = 1;
    } else {
      this.currentPage++;
    }
    this.updateDots();
    this.scrollToPage(this.currentPage);
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('testimonials-carousel', TestimonialsCarousel);
